import { Stack } from "@mui/material";
import * as React from "react";
import styled from "styled-components";
import Icon from "src/components/icon";
import gitlabIcon from "src/assets/icons/gitlab-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import githubIcon from "src/assets/icons/github-int";
import slackIcon from "src/assets/icons/slack-int";
import googleWorkspaceIcon from "src/assets/icons/google-workspace-int";
import jiraIcon from "src/assets/icons/jira-int";
import microsoftCalendarIcon from "src/assets/icons/microsoft-calendar-int";
import notionIcon from "src/assets/icons/notion-int";
import { IntegrationCategories } from "src/components/template/constants";
import type { TeamApi } from "src/scenes/teams/team/types";

const Description = styled.p`
    font-size: 11px;
    font-weight: 300;
    padding-bottom: 6px;
    margin: 0;
`;

const SubText = styled.p`
    font-size: 11px;
    font-weight: 300;
    padding-bottom: 6px;
    margin: 0;
    opacity: 0.8;
`;

const DashboardTooltipTeams = ({
    text,
    integrationsNeeded,
    teamApi,
}: {
    text: string;
    integrationsNeeded: IntegrationCategories;
    teamApi: TeamApi;
}) => {
    const teamSize = teamApi && teamApi.data && teamApi.data.membersCount;

    const getMembers = (
        integration: keyof NonNullable<TeamApi["data"]>["members"][number]["isIntegrated"]
    ): NonNullable<TeamApi["data"]>["members"] | undefined =>
        teamApi &&
        teamApi.data && [
            ...teamApi.data.members.filter((member) => member.isIntegrated[integration] === true),
            ...teamApi.data.tempMembers.filter(
                (member) => member.isIntegrated[integration] === true
            ),
        ];

    const githubMembers = getMembers("github");
    const gitlabMembers = getMembers("gitlab");
    const gitlabSelfHostedMembers = getMembers("gitlabSelfManaged");
    const jiraMembers = getMembers("jira");
    const notionMembers = getMembers("notion");
    const slackMembers = getMembers("slack");
    const googleMembers = getMembers("google");
    const microsoftMembers = getMembers("microsoft");
    const githubMembersCount = githubMembers && githubMembers.length;
    const gitlabMembersCount = gitlabMembers && gitlabMembers.length;
    const gitlabSelfHostedMembersCount = gitlabSelfHostedMembers && gitlabSelfHostedMembers.length;
    const jiraMembersCount = jiraMembers && jiraMembers.length;
    const notionMembersCount = notionMembers && notionMembers.length;
    const slackMembersCount = slackMembers && slackMembers.length;
    const googleMembersCount = googleMembers && googleMembers.length;
    const microsoftMembersCount = microsoftMembers && microsoftMembers.length;

    const calculateFill = (tool) => {
        if (tool === "slack") {
            if (slackMembersCount === teamSize) return "palette.primary.main";
            if (slackMembersCount === 0) return "palette.background.inactiveIcons";
            if (slackMembersCount && teamSize && slackMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "google") {
            if (googleMembersCount === teamSize) return "palette.primary.main";
            if (googleMembersCount === 0) return "palette.background.inactiveIcons";
            if (googleMembersCount && teamSize && googleMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "github") {
            if (githubMembersCount === teamSize) return "palette.primary.main";
            if (githubMembersCount === 0) return "palette.background.inactiveIcons";
            if (githubMembersCount && teamSize && githubMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "gitlab") {
            if (gitlabMembersCount === teamSize) return "palette.primary.main";
            if (gitlabMembersCount === 0) return "palette.background.inactiveIcons";
            if (gitlabMembersCount && teamSize && gitlabMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "gitlabSelfManaged") {
            if (gitlabSelfHostedMembersCount === teamSize) return "palette.primary.main";
            if (gitlabSelfHostedMembersCount === 0) return "palette.background.inactiveIcons";
            if (gitlabSelfHostedMembersCount && teamSize && gitlabSelfHostedMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "jira") {
            if (jiraMembersCount === teamSize) return "palette.primary.main";
            if (jiraMembersCount === 0) return "palette.background.inactiveIcons";
            if (jiraMembersCount && teamSize && jiraMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "notion") {
            if (notionMembersCount === teamSize) return "palette.primary.main";
            if (notionMembersCount === 0) return "palette.background.inactiveIcons";
            if (notionMembersCount && teamSize && notionMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        if (tool === "microsoft") {
            if (microsoftMembersCount === teamSize) return "palette.primary.main";
            if (microsoftMembersCount === 0) return "palette.background.inactiveIcons";
            if (microsoftMembersCount && teamSize && microsoftMembersCount < teamSize)
                return "palette.warning.main";
            return null;
        }
        return null;
    };
    const showIcons = () => {
        if (integrationsNeeded === IntegrationCategories.fragmentation)
            return (
                <Stack direction="row" spacing={2}>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={gitlabIcon} fill={calculateFill("gitlab")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {gitlabMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon
                            svg={gitlabSelfManagedIcon}
                            fill={calculateFill("gitlabSelfManaged")}
                        />
                        <SubText style={{ paddingTop: "8px" }}>
                            {gitlabSelfHostedMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={githubIcon} fill={calculateFill("github")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {githubMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column">
                        <Icon svg={slackIcon} fill={calculateFill("slack")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {slackMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={googleWorkspaceIcon} fill={calculateFill("google")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {googleMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={microsoftCalendarIcon} fill={calculateFill("microsoft")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {microsoftMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                </Stack>
            );
        if (integrationsNeeded === IntegrationCategories.git)
            return (
                <Stack direction="row" spacing={2}>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={gitlabIcon} fill={calculateFill("gitlab")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {gitlabMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon
                            svg={gitlabSelfManagedIcon}
                            fill={calculateFill("gitlabSelfManaged")}
                        />
                        <SubText style={{ paddingTop: "8px" }}>
                            {gitlabSelfHostedMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={githubIcon} fill={calculateFill("github")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {githubMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                </Stack>
            );
        if (integrationsNeeded === IntegrationCategories.collaboration)
            return (
                <Stack direction="row" spacing={2}>
                    <Stack direction="column">
                        <Icon svg={slackIcon} fill={calculateFill("slack")} />
                        {slackMembersCount && (
                            <SubText style={{ paddingTop: "8px" }}>
                                {slackMembersCount} / {teamSize}
                            </SubText>
                        )}
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={googleWorkspaceIcon} fill={calculateFill("google")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {googleMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={microsoftCalendarIcon} fill={calculateFill("microsoft")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {microsoftMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                </Stack>
            );
        if (integrationsNeeded === IntegrationCategories.slack)
            return (
                <Stack direction="column">
                    <Icon svg={slackIcon} fill={calculateFill("slack")} />
                    <SubText style={{ paddingTop: "8px" }}>
                        {slackMembersCount} / {teamSize}
                    </SubText>
                </Stack>
            );
        if (integrationsNeeded === IntegrationCategories.meetings)
            return (
                <Stack direction="row" spacing={2}>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={googleWorkspaceIcon} fill={calculateFill("google")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {googleMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={microsoftCalendarIcon} fill={calculateFill("microsoft")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {microsoftMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                </Stack>
            );
        if (integrationsNeeded === IntegrationCategories.pm)
            return (
                <Stack direction="row" spacing={2}>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={jiraIcon} fill={calculateFill("jira")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {jiraMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                    <Stack direction="column" justifyContent="center" alignItems="center">
                        <Icon svg={notionIcon} fill={calculateFill("notion")} />
                        <SubText style={{ paddingTop: "8px" }}>
                            {notionMembersCount} / {teamSize}
                        </SubText>
                    </Stack>
                </Stack>
            );
        return null;
    };

    return (
        <Stack direction="column" alignItems="left">
            <Description color="inherit">{text}</Description>
            <Stack
                direction="row"
                spacing={2}
                pt={0.5}
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="column">
                    <SubText color="inherit">Data source</SubText>
                    <SubText color="inherit">Active Members</SubText>
                </Stack>

                {showIcons()}
            </Stack>
        </Stack>
    );
};
export default DashboardTooltipTeams;
